/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited 2023.
 */

import { DpButton } from '@/components/blocks/DpLibrary/DpButton';
import {
	confirmationDialogActionsSX,
	confirmationDialogContentSX,
	confirmationDialogTitleSX,
} from '@/components/content/ConfirmationDialog/styles/content';
import { useLocalization } from '@/data/Localization';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { FC, ReactNode, useCallback } from 'react';

export type ConfirmationDialogText = {
	title?: ReactNode;
	message: ReactNode;
	cancel?: ReactNode;
	ok?: ReactNode;
};

export type ConfirmationDialogProps = {
	open?: boolean;
	onConfirm: () => Promise<unknown>;
	onCancel: () => Promise<unknown>;
	text: ConfirmationDialogText;
};

export const ConfirmationDialog: FC<ConfirmationDialogProps> = ({
	open = false,
	onCancel,
	onConfirm,
	text,
}) => {
	const confirmationNLS = useLocalization('Confirmation');
	const onCancelClick = useCallback(async () => {
		try {
			await onCancel();
		} catch (e) {}
	}, [onCancel]);

	const onOKClick = useCallback(async () => {
		try {
			await onConfirm();
		} catch (e) {}
	}, [onConfirm]);

	return (
		<Dialog
			aria-describedby="confirmation-dialog-content"
			disableEscapeKeyDown
			maxWidth="sm"
			open={open}
		>
			{text.title ? <DialogTitle sx={confirmationDialogTitleSX}>{text.title}</DialogTitle> : false}
			<DialogContent id="confirmation-dialog-content" sx={confirmationDialogContentSX}>
				{text.message}
			</DialogContent>
			<DialogActions sx={confirmationDialogActionsSX}>
				<DpButton
					variant="outlined"
					id="button-confirmation-dialog-cancel"
					data-testid="button-confirmation-dialog-cancel"
					onClick={onCancelClick}
				>
					{text.cancel ? text.cancel : confirmationNLS.CancelButton.t()}
				</DpButton>
				<DpButton
					variant="primary"
					id="button-confirmation-dialog-submit"
					data-testid="button-confirmation-dialog-submit"
					onClick={onOKClick}
				>
					{text.ok ? text.ok : confirmationNLS.SubmitButton.t()}
				</DpButton>
			</DialogActions>
		</Dialog>
	);
};
